import { LoadingOverlay, Stack } from "@mantine/core"
import { useForm } from "@mantine/form"
import { showNotification } from "@mantine/notifications"
import { CertificateStatus } from "@tfg/db/browser"
import { ErrorBox, DEFAULT_PAGE_SIZE } from "@tfg/ui"
import { zod4Resolver } from "mantine-form-zod-resolver"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import classes from "~/assets/css/Certificates.module.css"
import { queryClient } from "~/backend/queryClient"
import {
  CertificateByOrganizationDto,
  findCertificatesByOrganizationIdQueryKey,
  UpdateCertificateRequestDto,
  useFindCertificateTypesByOrganizationId,
  useUpdateCertificate,
} from "~/gen/api"
import { RenderField } from "~/pages/Admin/components/RenderField"
import { FormFieldConfig } from "~/pages/Admin/components/form.types"
import { CertificateFormSubmitButton } from "~/pages/Brand/Certificates/components/CertificateFormSubmitButton"
import { CertificateProductionSite } from "~/pages/Brand/Certificates/components/CertificateProductionSite"
import { ProcessedCertificateDescription } from "~/pages/Brand/Certificates/components/ProcessedCertificateDescription"
import {
  FilteredCertificate,
  getCertificateTemplate,
  getTemplateKeysForForm,
  getTypeOfKey,
} from "~/pages/Brand/Certificates/components/templates.ts"
import {
  getScoreTranslations,
  getTooltipTranslation,
  labelHolderTranslations,
  placeHolderTranslations,
} from "~/pages/Brand/Certificates/type.ts"
import { showAxiosError } from "~/utils/showAxiosError"

type Props = {
  certificate: CertificateByOrganizationDto
}

export function CertificateForm({ certificate }: Props) {
  const { t } = useTranslation("certificates")

  const { data } = useFindCertificateTypesByOrganizationId()
  const certificateTypes = data?.data

  const isCertificateTranscripted =
    CertificateStatus.PROCESSED === certificate.status

  const {
    mutateAsync: updateCertificate,
    isPending: isLoading,
    error,
  } = useUpdateCertificate({
    mutation: {
      onSuccess: async () => {
        showNotification({
          color: "green",
          message: t("modal.success"),
        })
        await queryClient.invalidateQueries({
          queryKey: findCertificatesByOrganizationIdQueryKey({
            pagination: {
              skip: 0,
              take: DEFAULT_PAGE_SIZE,
            },
          }),
        })
      },
      onError: (error) => {
        showAxiosError({
          error,
          showNotification: true,
        })
      },
    },
  })

  const initialValues = useMemo(
    () => ({
      ...certificate,
      reference: certificate.reference ?? "",
      name: certificate.name ?? certificate.type.name,
      typeId: certificate.type.id,
      productionSiteId: certificate.productionSite.id,
    }),
    [certificate]
  )
  const selectOptionsMap = useMemo(() => {
    return {
      typeId: certificateTypes?.map((type) => ({
        label: type.name,
        value: type.id,
      })),
      score: Object.entries(getScoreTranslations(t)).map(([score, label]) => ({
        label,
        value: score,
      })),
    }
  }, [certificateTypes, t])

  const getSelectOptions = useCallback(
    (key: string) => {
      if (key in selectOptionsMap) {
        return selectOptionsMap[key as keyof typeof selectOptionsMap]
      }
      return []
    },
    [selectOptionsMap]
  )

  const { fields, template } = useMemo(() => {
    const template = getCertificateTemplate(initialValues.type.category)
    const keys = getTemplateKeysForForm(template)
    const formFields: FormFieldConfig[] = keys.map((key) => {
      const type = getTypeOfKey(key)

      return {
        type,
        name: key,
        label: t(labelHolderTranslations[key as keyof FilteredCertificate]),
        placeholder: t(
          placeHolderTranslations[key as keyof FilteredCertificate]
        ),
        customClass: classes,
        options: getSelectOptions(key),
        tooltip: getTooltipTranslation(key, t),
      }
    })
    return { fields: formFields, template }
  }, [initialValues, t, getSelectOptions])

  const form = useForm<UpdateCertificateRequestDto>({
    validate: zod4Resolver(template),
    initialValues,
  })

  const submit = async () => {
    form.setFieldValue("status", CertificateStatus.PROCESSED)
    await updateCertificate({ id: certificate.id, data: form.getValues() })
  }

  return (
    <Stack gap="sm" h="100%" mt="md" style={{ overflow: "hidden" }}>
      {isCertificateTranscripted ? (
        <ProcessedCertificateDescription certificate={certificate} />
      ) : null}
      <form
        onSubmit={form.onSubmit((values) =>
          updateCertificate({ id: certificate.id, data: values })
        )}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <LoadingOverlay visible={isLoading} />
        <ErrorBox error={error} />
        {certificate.productionSite && (
          <CertificateProductionSite certificate={certificate} />
        )}
        <Stack
          flex={1}
          mih={0}
          gap="var(--mantine-spacing-xxs)"
          style={{ overflow: "auto" }}
        >
          {fields.map((field) => (
            <div key={field.name}>
              <RenderField field={field} form={form} />
            </div>
          ))}
        </Stack>
        <Stack
          pos="sticky"
          bottom={0}
          w="100%"
          display="flex"
          justify="center"
          align="center"
          bg="var(--mantine-color-brand-background)"
          style={{
            paddingBlock: "var(--mantine-spacing-md)",
            zIndex: 1,
          }}
        >
          <CertificateFormSubmitButton
            form={form}
            submit={submit}
            isLoading={isLoading}
          />
        </Stack>
      </form>
    </Stack>
  )
}
